import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import ExercisePrompt from '../../../student/components/exercises/ExercisePrompt';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import { cardSx } from '../../../student/studentStyles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { QuizExercise, QuizOption, shows } from '../../../student/mockLessonContent';
import { BLUE, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import ImageZoom, { ZoomButton } from '../../../student/components/ImageZoom';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import OptionalSupport from '../../../student/components/exercises/OptionalSupport';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';
import { shuffledOrder } from './quizShuffle';

interface QuizExerciseCardProps {
    exercise: QuizExercise;
    onReady: ExerciseReady;
}

const labelByMode: Record<QuizExercise['mode'], string> = {
    text: 'QUICK CHECK',
    picture: 'PICTURE CHECK',
    audio: 'LISTENING CHECK',
};

const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
const WRONG_FLASH_MS = 650;

// "Quiz: Select One" (text or picture answers), "Quiz: Select One Audio" (the
// choices are clips, revealed once answered) and "Quiz: Select One, then Speak".
// As in legacy, a wrong pick gets the teacher's response and the choices are
// reshuffled for another try; after two misses the student may skip.
export default function QuizExerciseCard({ exercise, onReady }: QuizExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [round, setRound] = React.useState(0);
    const [order, setOrder] = React.useState(() => shuffledOrder(exercise.options.length, exercise.id, 0));
    const [flashWrong, setFlashWrong] = React.useState<number | null>(null);
    const [misses, setMisses] = React.useState(0);
    const [revealed, setRevealed] = React.useState<number[]>([]);
    const [selectedAnswer, setSelectedAnswer] = React.useState<number | null>(null);
    const [solved, setSolved] = React.useState(false);
    const [revealedAnswer, setRevealedAnswer] = React.useState(false);
    const [heardOptions, setHeardOptions] = React.useState<number[]>([]);
    const [playingOption, setPlayingOption] = React.useState<number | null>(null);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const isAudio = exercise.mode === 'audio';
    const isPicture = exercise.mode === 'picture';

    const handlePick = (index: number) => {
        if (solved || flashWrong != null || (isAudio && (!heardOptions.includes(index) || playingOption != null))) {
            return;
        }
        setRevealed(prev => (prev.includes(index) ? prev : [...prev, index]));
        setSelectedAnswer(index);
        if (index === exercise.correctIndex) {
            setSolved(true);
            if (!exercise.speakAfter) {
                onReady('done');
            }
            return;
        }
        const nextMisses = misses + 1;
        setMisses(nextMisses);
        setFlashWrong(index);
        if (nextMisses === 2 && !exercise.speakAfter) {
            onReady('skip');
        }
        // Show the miss briefly, then reshuffle for the next try.
        timeoutRef.current = window.setTimeout(() => {
            setFlashWrong(null);
            setRound(value => value + 1);
            setOrder(previous => shuffledOrder(exercise.options.length, exercise.id, round + 1, previous));
        }, WRONG_FLASH_MS);
    };

    const renderOptionBody = (option: QuizOption, index: number) => {
        if (isPicture) {
            return (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Typography role="img" aria-label={option.english ?? `Picture ${index + 1}`} sx={{ fontSize: desktop ? 54 : 46, lineHeight: 1.1 }}>
                        {option.emoji}
                    </Typography>
                    {option.hanzi && shows(exercise, 'characters') && (
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 0.75 }}>{option.hanzi}</Typography>
                    )}
                    {option.pinyin && shows(exercise, 'characters') && shows(exercise, 'pinyin') && (
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>{option.pinyin}</Typography>
                    )}
                    {option.english && shows(exercise, 'english') && (
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{option.english}</Typography>
                    )}
                </Box>
            );
        }
        const showText = !isAudio || solved || revealed.includes(index);
        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {isAudio && option.displayWord && (
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 22, color: NAVY, lineHeight: 1.2 }}>{option.displayWord}</Typography>
                )}
                {showText ? (
                    <>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: isAudio && option.displayWord ? 14 : 18, color: isAudio && option.displayWord ? SLATE : NAVY }}>
                            {option.hanzi}
                        </Typography>
                        {option.pinyin && shows(exercise, 'pinyin') && (
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>{option.pinyin}</Typography>
                        )}
                        {option.english && shows(exercise, 'english') && (
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>{option.english}</Typography>
                        )}
                    </>
                ) : null}
                {isAudio && (!solved || playingOption === index) && (
                    <Typography id={`${exercise.id}-audio-status-${index}`} aria-live="polite" sx={{ fontFamily: INTER, fontSize: 14, color: playingOption === index ? BLUE : SLATE }}>
                        {playingOption === index ? 'Playing…' : heardOptions.includes(index) ? 'Choose this recording' : 'Play audio first'}
                    </Typography>
                )}
            </Box>
        );
    };

    const columns = isPicture
        ? `repeat(${desktop ? Math.min(exercise.options.length, 4) : 2}, minmax(0, 1fr))`
        : desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr';

    return (
        <Box sx={exercise.speakAfter ? { ...cardSx, overflow: 'hidden' } : undefined}>
            {exercise.speakAfter && <SpeakingHero label="QUIZ & SPEAK" title={exercise.prompt} />}
            <Box sx={exercise.speakAfter ? { p: desktop ? 3 : 2.5 } : undefined}>
            {!exercise.speakAfter && <ExerciseLabel>{labelByMode[exercise.mode]}</ExerciseLabel>}
            {exercise.teacher?.intro && <Box sx={{ mb: 2 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
            {exercise.promptEmoji && (
                <Box sx={{ width: 150, mx: 'auto', mb: 2 }}>
                    <ImageZoom emoji={exercise.promptEmoji} alt="Question picture" height={128} fontSize={64} rounded />
                </Box>
            )}
            {!exercise.speakAfter && <ExercisePrompt text={exercise.prompt} centered={Boolean(exercise.promptEmoji)} />}
            {exercise.teacherQuestion && (
                <Stack direction="row" alignItems="center" spacing={1.25} justifyContent={exercise.promptEmoji ? 'center' : 'flex-start'} sx={{ mt: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontSize: 18, color: NAVY }}>{exercise.teacherQuestion.hanzi}</Typography>
                        {shows(exercise, 'pinyin') && <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE }}>{exercise.teacherQuestion.pinyin}</Typography>}
                    </Box>
                    <AudioPlayButton ariaLabel="Play the teacher's question" size={36} />
                </Stack>
            )}

            <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: columns, gap: 1.25 }}>
                {order.map(index => {
                    const option = exercise.options[index];
                    const isWrong = flashWrong === index;
                    const isCorrect = solved && index === exercise.correctIndex;
                    return (
                        <Box
                            key={`${exercise.id}-${index}`}
                            sx={{
                                position: 'relative',
                                display: 'flex', alignItems: 'center', gap: 1,
                                borderRadius: '16px',
                                backgroundColor: isCorrect ? `${GREEN}14` : isWrong ? `${RED}14` : isPicture || exercise.speakAfter ? SURFACE_SOFT : '#fff',
                                border: `2px solid ${isCorrect ? GREEN : isWrong ? RED : 'transparent'}`,
                                boxShadow: isPicture || exercise.speakAfter ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
                                opacity: solved && !isCorrect ? 0.5 : 1,
                                transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                                pr: isAudio ? 1.5 : 0,
                            }}
                        >
                            <ButtonBase
                                onClick={() => handlePick(index)}
                                disabled={solved || flashWrong != null || (isAudio && (!heardOptions.includes(index) || playingOption != null))}
                                aria-label={isAudio ? `Choose option ${optionLetters[order.indexOf(index)]}` : isPicture ? `Choose picture: ${option.english ?? index + 1}` : undefined}
                                aria-describedby={isAudio && (!solved || playingOption === index) ? `${exercise.id}-audio-status-${index}` : undefined}
                                sx={{
                                    flex: 1, minWidth: 0, p: isPicture ? 2.5 : 2, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 1.5,
                                    textAlign: 'left', minHeight: isPicture ? (desktop ? 130 : 112) : undefined,
                                }}
                            >
                                {isAudio && (
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: `${BLUE}14`,
                                    }}>
                                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE }}>
                                            {optionLetters[order.indexOf(index)]}
                                        </Typography>
                                    </Box>
                                )}
                                {renderOptionBody(option, index)}
                                {isCorrect && !isPicture && <CheckCircleRoundedIcon sx={{ color: GREEN }} />}
                            </ButtonBase>
                            {(isAudio || shows(exercise, 'audio')) && <AudioPlayButton
                                ariaLabel={`Play option ${optionLetters[order.indexOf(index)]}`} size={40}
                                disabled={isAudio && (flashWrong != null || (playingOption != null && playingOption !== index))}
                                onPlayingChange={isAudio ? playing => setPlayingOption(playing ? index : null) : undefined}
                                onPlayed={isAudio ? () => setHeardOptions(previous => previous.includes(index) ? previous : [...previous, index]) : undefined}
                            />}
                            {isPicture && option.emoji && <ZoomButton emoji={option.emoji} alt={option.english ?? 'Answer picture'} />}
                        </Box>
                    );
                })}
            </Box>
            {(solved || flashWrong != null || misses > 0) && (
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: solved ? SUCCESS_TEXT : ERROR_TEXT, mt: 1.5 }}>
                    {solved
                        ? revealedAnswer ? 'Here is the answer. Now say it aloud.' : exercise.teacher?.correct ?? "对了! That's it."
                        : `${exercise.teacher?.incorrect ?? 'Not quite.'} The choices have been shuffled — try again.`}
                </Typography>
            )}
            {!solved && misses >= 2 && exercise.speakAfter && (
                <Button disabled={flashWrong != null} onClick={() => { setSelectedAnswer(exercise.correctIndex); setRevealedAnswer(true); setSolved(true); }} sx={{ mt: 1, minHeight: 44 }}>
                    Skip question — show me the answer to say
                </Button>
            )}
            {!solved && misses >= 2 && !exercise.speakAfter && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.75 }}>
                    Still stuck? You can skip this one — Continue now reads “Skip”.
                </Typography>
            )}

            {/* Support explains the most recent choice, including an incorrect choice. */}
            {selectedAnswer != null && <OptionalSupport content={exercise.options[selectedAnswer]} display={exercise.display} />}

            {/* "Quiz: Select One, then Speak": the answer's own sentence is said out loud. */}
            {exercise.speakAfter && solved && (
                <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${RING_TRACK}` }}>
                    <TeacherNote text={exercise.teacher?.askToSpeak ?? exercise.speakAfter.instruction} />
                    <Box sx={{ mt: 1.5, p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 18, color: NAVY }}>
                                {exercise.speakAfter.sentence.hanzi}
                            </Typography>
                            {(exercise.speakAfter.showPinyin ?? false) && exercise.speakAfter.sentence.pinyin && <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                {exercise.speakAfter.sentence.pinyin}
                            </Typography>}
                            {shows(exercise, 'english') && exercise.speakAfter.sentence.english && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                    {exercise.speakAfter.sentence.english}
                                </Typography>
                            )}
                        </Box>
                        <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                    </Box>
                    <RecordAnswerPanel
                        grades={exercise.speakAfter.sentence.grades}
                        takes={exercise.speakAfter.sentence.takes}
                        correctText={exercise.teacher?.spokenCorrect}
                        incorrectText={exercise.teacher?.spokenIncorrect}
                        onComplete={() => onReady('done')}
                        onSkippable={() => onReady('skip')}
                    />
                </Box>
            )}
            <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
